import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  useTheme,
  alpha,
  Alert,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Chip,
  Tooltip,
  CircularProgress,
  Switch,
  Avatar,
  Link,
  InputAdornment
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    is_admin: false,
    is_active: true
  });
  const [avatar, setAvatar] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formError, setFormError] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    if (!userInfo.is_admin) {
      navigate('/history');
      return;
    }
    fetchUsers();
  }, [navigate]);

  useEffect(() => {
    // Filtrar usuários quando o termo de busca ou a lista de usuários mudar
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const lowercasedSearch = searchTerm.toLowerCase();
      const filtered = users.filter(
        user => 
          user.username.toLowerCase().includes(lowercasedSearch) || 
          user.email.toLowerCase().includes(lowercasedSearch)
      );
      setFilteredUsers(filtered);
    }
    // Resetar para a primeira página quando a busca mudar
    setPage(0);
  }, [searchTerm, users]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/users');

      if (response.status === 403) {
        setError('Você não tem permissão para acessar esta página.');
        return;
      }

      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      setError('Erro ao carregar a lista de usuários');
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        is_admin: user.is_admin,
        is_active: user.is_active
      });
      if (user.avatar_url) {
        setPreviewUrl(`${process.env.REACT_APP_API_URL}${user.avatar_url}`);
      } else {
        setPreviewUrl(null);
      }
    } else {
      setSelectedUser(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        is_admin: false,
        is_active: true
      });
      setPreviewUrl(null);
    }
    setAvatar(null);
    setDialogOpen(true);
    setFormError('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setFormError('');
  };

  const handleOpenImportDialog = () => {
    setCsvFile(null);
    setImportResults(null);
    setImportDialogOpen(true);
    setFormError('');
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setCsvFile(null);
    setImportResults(null);
    setFormError('');
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setFormError('A imagem deve ter no máximo 5MB');
        return;
      }
      setAvatar(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCsvFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        setFormError('Por favor, selecione um arquivo CSV válido');
        return;
      }
      setCsvFile(file);
      setFormError('');
    }
  };

  const handleSubmit = async () => {
    try {
      if (!formData.email || !formData.username || (!selectedUser && !formData.password)) {
        setFormError('Por favor, preencha todos os campos obrigatórios.');
        return;
      }

      console.log('Dados do formulário a serem enviados:', formData);
      
      // Verificando se estamos enviando um arquivo
      const hasFile = avatar !== null;
      
      if (hasFile) {
        // Se temos um arquivo, usamos FormData
        const formDataToSend = new FormData();
        formDataToSend.append('username', formData.username);
        formDataToSend.append('email', formData.email);
        if (formData.password) {
          formDataToSend.append('password', formData.password);
        }
        
        // Convertendo boolean para string para garantir que seja enviado corretamente
        formDataToSend.append('is_admin', formData.is_admin.toString());
        formDataToSend.append('is_active', formData.is_active.toString());
        
        formDataToSend.append('avatar', avatar);
        
        // Log dos dados que serão enviados
        console.log('FormData a ser enviado:');
        for (let pair of formDataToSend.entries()) {
          console.log(pair[0] + ': ' + pair[1]);
        }
        
        let updatedUser;
        if (selectedUser) {
          console.log(`Enviando PUT para /users/${selectedUser.id} com FormData`);
          const response = await api.put(`/users/${selectedUser.id}`, formDataToSend);
          console.log('Resposta do servidor (PUT):', response);
          updatedUser = response.data;
        } else {
          console.log('Enviando POST para /users com FormData');
          const response = await api.post('/users', formDataToSend);
          console.log('Resposta do servidor (POST):', response);
          updatedUser = response.data;
        }
        
        console.log('Dados do usuário atualizado/criado:', updatedUser);
      } else {
        // Se não temos arquivo, usamos JSON para garantir que os tipos sejam preservados
        const jsonData = {
          username: formData.username,
          email: formData.email,
          is_admin: formData.is_admin,
          is_active: formData.is_active
        };
        
        if (formData.password) {
          jsonData.password = formData.password;
        }
        
        console.log('JSON a ser enviado:', jsonData);
        
        let updatedUser;
        if (selectedUser) {
          console.log(`Enviando PUT para /users/${selectedUser.id} com JSON`);
          const response = await api.put(`/users/${selectedUser.id}`, jsonData);
          console.log('Resposta do servidor (PUT):', response);
          updatedUser = response.data;
        } else {
          console.log('Enviando POST para /users com JSON');
          const response = await api.post('/users', jsonData);
          console.log('Resposta do servidor (POST):', response);
          updatedUser = response.data;
        }
        
        console.log('Dados do usuário atualizado/criado:', updatedUser);
      }
      
      // Vamos buscar os usuários novamente para garantir que temos os dados mais recentes
      console.log('Buscando lista atualizada de usuários');
      await fetchUsers();

      handleCloseDialog();
      setSuccess(selectedUser ? 'Usuário atualizado com sucesso!' : 'Usuário criado com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      console.error('Detalhes do erro:', error.response?.data);
      setFormError(error.message || 'Erro ao salvar usuário');
    }
  };

  const handleImportSubmit = async () => {
    if (!csvFile) {
      setFormError('Por favor, selecione um arquivo CSV para importar');
      return;
    }

    try {
      setImportLoading(true);
      setFormError('');
      
      const formData = new FormData();
      formData.append('file', csvFile);

      const response = await api.post('/users/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResults(response.data);
      await fetchUsers();
      setSuccess(`Importação concluída: ${response.data.success_count} usuários importados com sucesso.`);
      
      // Fechar o modal após 3 segundos se a importação for bem-sucedida
      if (response.data.success_count > 0 && response.data.error_count === 0) {
        setTimeout(() => {
          handleCloseImportDialog();
        }, 3000);
      }
    } catch (error) {
      console.error('Erro na importação:', error);
      setFormError(error.response?.data?.detail || 'Erro ao importar usuários');
    } finally {
      setImportLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    // Criar conteúdo CSV
    const headers = 'senha,User name,email,tipo\n';
    const exampleRow = '123456,Nome do Usuário,usuario@exemplo.com,Usuário\n';
    const exampleRow2 = '123456,Admin Exemplo,admin@exemplo.com,Administrador\n';
    const csvContent = headers + exampleRow + exampleRow2;
    
    // Criar blob e link para download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'modelo_importacao_usuarios.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      const formData = new FormData();
      formData.append('is_active', !currentStatus);

      const response = await api.put(`/users/${userId}`, formData);
      const updatedUser = response.data;
      
      // Atualiza o usuário na lista local
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? updatedUser : user
        )
      );
      
      // Atualiza também a lista filtrada
      setFilteredUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? updatedUser : user
        )
      );

      setSuccess('Status do usuário alterado com sucesso!');
    } catch (error) {
      setError('Erro ao alterar status do usuário');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      try {
        await api.delete(`/users/${userId}`);
        
        // Remove o usuário da lista local
        setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
        
        // Remove também da lista filtrada
        setFilteredUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
        
        setSuccess('Usuário excluído com sucesso!');
      } catch (error) {
        setError('Erro ao excluir usuário');
        console.error('Erro:', error);
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              color: theme.palette.mode === 'dark' ? '#fff' : '#1a1f2c',
              fontWeight: 600
            }}
          >
            Gerenciamento de Usuários
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={handleOpenImportDialog}
              sx={{
                py: 1.5,
                px: 3,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem'
              }}
            >
              Importar Usuários
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                py: 1.5,
                px: 3,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem'
              }}
            >
              Novo Usuário
            </Button>
          </Box>
        </Box>
        <Divider sx={{ 
          borderColor: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.1)' 
            : 'rgba(0, 0, 0, 0.1)',
          mb: 2
        }} />

        {/* Barra de busca */}
        <TextField
          fullWidth
          placeholder="Buscar por nome ou email..."
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton onClick={clearSearch} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              borderRadius: 2,
              backgroundColor: theme.palette.mode === 'dark' 
                ? alpha(theme.palette.background.paper, 0.6)
                : alpha(theme.palette.background.paper, 0.8),
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' 
                  ? alpha(theme.palette.background.paper, 0.8)
                  : alpha(theme.palette.background.paper, 1)
              }
            }
          }}
        />
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
          onClose={() => setSuccess('')}
        >
          {success}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.paper, 0.6)
            : theme.palette.background.paper,
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          border: '1px solid',
          borderColor: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.1)'
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Nome de Usuário</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length > 0 ? (
                filteredUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.id}</TableCell>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Switch
                          checked={user.is_active}
                          onChange={() => handleToggleActive(user.id, user.is_active)}
                          color="success"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.is_admin ? "Administrador" : "Usuário"}
                          color={user.is_admin ? "primary" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar usuário">
                          <IconButton
                            onClick={() => handleOpenDialog(user)}
                            size="small"
                            sx={{ mr: 1 }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir usuário">
                          <IconButton
                            onClick={() => handleDeleteUser(user.id)}
                            size="small"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" sx={{ py: 3 }}>
                      {searchTerm ? 'Nenhum usuário encontrado para esta busca.' : 'Nenhum usuário cadastrado.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Itens por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>

      {/* Modal para adicionar/editar usuário */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedUser ? 'Editar Usuário' : 'Novo Usuário'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              mb: 2 
            }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  src={previewUrl}
                  sx={{
                    width: 120,
                    height: 120,
                    mb: 2,
                    border: '4px solid',
                    borderColor: theme.palette.primary.main
                  }}
                >
                  {formData.username ? formData.username[0].toUpperCase() : 'U'}
                </Avatar>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="avatar-upload"
                  type="file"
                  onChange={handleAvatarChange}
                />
                <label htmlFor="avatar-upload">
                  <Tooltip title="Alterar foto (máx. 5MB)">
                    <IconButton
                      component="span"
                      sx={{
                        position: 'absolute',
                        right: 0,
                        bottom: 16,
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark
                        }
                      }}
                    >
                      <PhotoCamera />
                    </IconButton>
                  </Tooltip>
                </label>
              </Box>
            </Box>

            <TextField
              fullWidth
              label="Nome de Usuário"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="E-mail"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Senha"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!selectedUser}
              helperText={selectedUser ? "Deixe em branco para manter a senha atual" : ""}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_admin}
                  onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                />
              }
              label="Administrador"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Ativo"
            />
            {formError && (
              <Alert severity="error">{formError}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedUser ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para importação em massa de usuários */}
      <Dialog
        open={importDialogOpen}
        onClose={handleCloseImportDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Importar Usuários em Massa
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Alert severity="info">
              Faça upload de um arquivo CSV contendo os usuários a serem importados. 
              O arquivo deve conter as colunas: <strong>senha</strong>, <strong>User name</strong>, <strong>email</strong> e <strong>tipo</strong> (Usuário ou Administrador).
            </Alert>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              gap: 2,
              p: 3,
              border: '2px dashed',
              borderColor: theme.palette.primary.main,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.primary.main, 0.05)
            }}>
              <CloudUploadIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />
              
              <Typography variant="body1" align="center">
                Arraste e solte o arquivo CSV aqui ou clique para selecionar
              </Typography>
              
              <input
                accept=".csv"
                style={{ display: 'none' }}
                id="csv-upload"
                type="file"
                onChange={handleCsvFileChange}
              />
              <label htmlFor="csv-upload">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<UploadFileIcon />}
                >
                  Selecionar Arquivo
                </Button>
              </label>
              
              {csvFile && (
                <Typography variant="body2" color="primary">
                  Arquivo selecionado: {csvFile.name}
                </Typography>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadTemplate}
              >
                Baixar Modelo de Planilha
              </Button>
            </Box>
            
            {importResults && (
              <Alert 
                severity={importResults.error_count > 0 ? "warning" : "success"}
                sx={{ mt: 2 }}
              >
                <Typography variant="subtitle2">
                  Resultado da importação:
                </Typography>
                <Typography variant="body2">
                  • Usuários importados com sucesso: {importResults.success_count}
                </Typography>
                {importResults.error_count > 0 && (
                  <Typography variant="body2">
                    • Usuários com erro: {importResults.error_count}
                  </Typography>
                )}
                {importResults.errors && importResults.errors.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2">Detalhes dos erros:</Typography>
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      {importResults.errors.slice(0, 5).map((error, index) => (
                        <li key={index}>
                          <Typography variant="body2">
                            Linha {error.row}: {error.message}
                          </Typography>
                        </li>
                      ))}
                      {importResults.errors.length > 5 && (
                        <li>
                          <Typography variant="body2">
                            ... e mais {importResults.errors.length - 5} erros.
                          </Typography>
                        </li>
                      )}
                    </ul>
                  </Box>
                )}
              </Alert>
            )}
            
            {formError && (
              <Alert severity="error">{formError}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog}>Cancelar</Button>
          <Button 
            onClick={handleImportSubmit} 
            variant="contained"
            disabled={!csvFile || importLoading}
            startIcon={importLoading ? <CircularProgress size={20} /> : null}
          >
            {importLoading ? 'Importando...' : 'Importar Usuários'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserManagement; 